import {
  getReferralByTackleId,
  getReferralEvents,
} from 'packages/tackle-api/src/co-sell-msft/referrals/getReferral';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Loader } from '@tackle-io/platform-ui';
import { useCallback, useEffect } from 'react';

const REFERRAL_QUERY_KEY = 'referral-by-tackle-id';
type Events = Awaited<ReturnType<typeof getReferralEvents>>['events'];

export const operationIsCompleteCheck = ({
  events,
  operationId,
}: {
  events?: Events;
  /** pending request id */
  operationId?: string;
}) => {
  if (!events || !operationId)
    return {
      event: null,
      isComplete: false,
      latestCloudResponseTimestamp: null,
    };

  const event = events?.find((x) => operationId === x.eventId) ?? null;

  /**
   * each cloudRequest should have a corresponding cloudResponse,
   * if not, the cloudRequest has not received a response yet and
   * would be considered pending.
   *  */
  const eventRequestResponseCountMatches =
    /** ensure the event is a createIPReferral  */
    event.operation === 'createIPReferral' &&
    event?.cloudResponses?.length > 0 &&
    event?.cloudResponses?.length === event?.cloudRequests?.length;

  if (!eventRequestResponseCountMatches) {
    return {
      isComplete: false,
      event,
      latestCloudResponseTimestamp: null,
    };
  }

  const cloudResponse = event?.cloudResponses?.[0];
  return {
    isComplete: eventRequestResponseCountMatches,
    latestCloudResponseTimestamp: cloudResponse.timestamp,
    event,
  };
};

interface CreationPendingProps<T extends string> {
  tackleId: T;
  requestId: string;
  onError: (tackleId: T) => void;
  onSuccess: (tackleId: T) => void;
}

function CreationPending({
  onError,
  onSuccess,
  tackleId,
  requestId,
}: CreationPendingProps<typeof tackleId>) {
  const queryClient = useQueryClient();

  const { data: operationEvent } = useQuery({
    queryKey: ['CREATION_PENDING_MSFT_REFERRAL_EVENTS', tackleId, requestId],
    queryFn: () => getReferralEvents({ tackleId }),
    select: (data) =>
      operationIsCompleteCheck({
        events: data?.events,
        operationId: requestId,
      }),
    refetchInterval: (data) => {
      /** poll events until operation is complete */
      return !data?.isComplete ? 1000 : false;
    },
  });

  const operationIsComplete = !!operationEvent?.isComplete;

  const { data: referral } = useQuery({
    queryKey: ['CREATION_PENDING_MSFT_REFERRAL', requestId, tackleId],
    queryFn: () => getReferralByTackleId({ tackleId }),
    enabled: operationIsComplete,
    refetchInterval: (data) => {
      return !data?.isCreatedInCloud ? 2000 : false;
    },
  });

  const handleCreationError = useCallback(() => {
    onError(tackleId);
  }, [onError, tackleId]);

  const handleCreationSuccess = useCallback(() => {
    /** if cloud id is created, set the opportunity data to the opportunity query */
    queryClient.setQueryData([REFERRAL_QUERY_KEY, tackleId], referral);
    onSuccess(tackleId);
  }, [onSuccess, queryClient, tackleId, referral]);

  const referralDataIsUpToDateWithEvents =
    operationIsComplete &&
    operationEvent?.latestCloudResponseTimestamp &&
    referral?.metadata?.lastModifiedDate
      ? new Date(referral?.metadata?.lastModifiedDate) >=
        new Date(operationEvent?.latestCloudResponseTimestamp)
      : false;

  useEffect(() => {
    if (referral?.isCreatedInCloud) {
      handleCreationSuccess();
    }

    /** wait until:
     * - referralDataIsUpToDateWithEvents
     * - there are no pendingRequestIds, as we want to be sure we have the latest
     * */
    if (
      !referralDataIsUpToDateWithEvents ||
      !!referral?.metadata?.pendingRequestItemIds?.length
    )
      return;

    /** When referral data is up to date with operationEvent lastModifedDate,
     *  check if the referral has been created in the cloud.
     *  don't use the event to check if it was created in the cloud,
     *  bc there is a delay updating the referral data.
     */
    if (!referral?.isCreatedInCloud && !!referral?.metadata?.hasCloudErrors) {
      handleCreationError();
      return;
    }
  }, [
    handleCreationError,
    handleCreationSuccess,
    referral,
    referral?.isCreatedInCloud,
    referralDataIsUpToDateWithEvents,
  ]);

  return <Loader />;
}

export default CreationPending;
